<template>
  <!-- id bleibt „inspire“: die globale #app-Regel der Anmeldung greift hier nicht -->
  <v-app id="inspire" class="shell" :class="shellClasses">
    <!-- Sprungmarke für die Tastatur: erst beim Fokus sichtbar, setzt den Fokus ins Blatt -->
    <a class="shell-skip" href="#inhalt" @click.prevent="skipToContent">Zum Inhalt springen</a>

    <!-- Shell „Mobility Hub“ (Darstellung im Benutzermenü): Kopfleiste, Kerbe, Navigation, bündige Inhaltsfläche -->
    <template v-if="hub">
      <app-hub-topbar :mobile="hubMobile" @toggle-nav="toggleHubNav" @sign-out="signOut" />
      <app-hub-notch />
      <div v-if="!hubMobile" class="hub-nav">
        <app-hub-sidebar
          :groups="navigation"
          :active-path="activePath"
          :active-group-id="activeGroupId"
          :collapsed="hubCollapsed"
          @toggle-collapse="toggleHubCollapse"
        />
      </div>
      <transition name="hub-scrim">
        <div v-if="hubMobile && hubNavOpen" class="hub-scrim" @click="closeHubNav"></div>
      </transition>
      <transition name="hub-drawer">
        <div
          v-if="hubMobile && hubNavOpen"
          ref="hubDrawer"
          class="hub-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          tabindex="-1"
          @keydown.esc="closeHubNav"
        >
          <app-hub-sidebar
            mobile
            :groups="navigation"
            :active-path="activePath"
            :active-group-id="activeGroupId"
            @navigate="closeHubNav"
          />
        </div>
      </transition>
      <v-main class="shell-main hub-main">
        <div id="inhalt" ref="sheet" tabindex="-1" class="hub-content"><router-view /></div>
      </v-main>
    </template>

    <!-- klassische Shell -->
    <template v-else>
      <v-navigation-drawer
        v-if="!handy"
        app
        permanent
        floating
        touchless
        tag="div"
        :width="railWidth"
        class="shell-nav"
      >
        <app-sidebar
          v-if="mode === 'sidebar'"
          :groups="navigation"
          :active-path="activePath"
          :active-group-id="activeGroupId"
          @sign-out="signOut"
        />
        <app-rail
          v-else-if="mode === 'rail'"
          :groups="navigation"
          :active-path="activePath"
          :active-group-id="activeGroupId"
          :can-expand="wide"
          @sign-out="signOut"
        />
        <app-touch-rail
          v-else
          :groups="navigation"
          :active-path="activePath"
          :active-group-id="activeGroupId"
          @sign-out="signOut"
        />
      </v-navigation-drawer>

      <!-- v-main als div: <main> ist erst der Inhalt (#inhalt), damit Systemleiste bzw. App-Leiste (<header>) außerhalb
           davon als Kopf der Seite gelten; eine einzige router-view-Position, damit Seiten bei Moduswechseln nicht neu
           montiert werden -->
      <v-main class="shell-main" tag="div">
        <app-phone-bar v-if="handy" :title="phoneTitle" :back="!!moreGroup" @back="closeMoreGroup" />
        <div v-show="!(handy && moreOpen)" :class="handy ? 'shell-phone-content' : 'shell-sheet-frame'">
          <!-- Blatt: oben die Systemleiste, darunter der Inhalt, der selbst scrollt (Handy: nur der Inhalt) -->
          <div class="shell-body" :class="{ 'shell-sheet': !handy }">
            <app-system-bar v-if="!handy" />
            <main id="inhalt" ref="sheet" tabindex="-1" class="shell-content"><router-view /></main>
          </div>
        </div>
        <app-more-menu
          v-if="handy && moreOpen"
          :groups="navigation"
          :active-path="activePath"
          @navigate="closeMore"
          @sign-out="signOut"
        />
      </v-main>

      <app-tab-bar
        v-if="handy"
        :groups="navigation"
        :active-path="activePath"
        @more="openMore"
        @select="onTabSelect"
      />
    </template>
    <app-tenant-switch v-if="canSwitchTenant" v-model="tenantSwitchOpen" />
    <!-- Nachrichten (Hinweis auf die neue Version, Kundenwunsch 13.10.2026): öffnet sich beim Start, bis sie per Schalter
         abgeschaltet ist; jederzeit über die Glocke oben rechts bzw. das Mehr-Menü -->
    <app-message-dialog />
  </v-app>
</template>

<script>
// Shell der Verwaltung (Spezifikation 2.11): Navigation links (Seitenleiste 232 / Rail 60 / Touch-Rail 104; oben das
// SIMA-Logo, unten das Profil) und Blatt mit 8 px Abstand oben, rechts und unten. Oben im Blatt die Systemleiste
// (AppSystemBar: Mandant, Brotkrumen, Suche, Verbindungsstatus), darunter der Inhalt, der selbst scrollt; auf dem Handy
// (< 600 px) App-Leiste, untere Navigation und Mehr-Menü (Ebene ohne Route).
// Dazu: Sprungmarke „Zum Inhalt springen“, Strg/⌘+B klappt die Seitenleiste ein und aus (Maus ab 1264), Dialog
// „Mandant wechseln“ (alle angemeldeten Benutzer).
// Zweite Shell zum Vergleich: „Mobility Hub“ ($shell.variant 'hub', Benutzermenü „Darstellung“) mit Kopfleiste, Kerbe
// als Produktwechsel, Navigation 256/48 und bündiger Inhaltsfläche (src/components/hub, src/css/redesign/hub.css);
// unter 768 px Navigation als Schublade. Seiten, Logik und Farben teilen sich beide Shells.
// Modus: Handy → kein Drawer; Touch-Dichte → Touch-Rail; sonst ab 1264 Seitenleiste (einklappbar), darunter Rail.
// Farbschema hell/dunkel ($shell.theme, Kundenwunsch 12.10.2026): gilt nur, solange die Shell eingehängt ist
// (src/shell/farbschema.js); die Anmeldung bleibt hell.
// Websocket verbinden/trennen und Abmelden gibt es nur hier.
import { Auth } from "aws-amplify";
import websocket from "@/aws/websocket";
import AppSidebar from "@/components/shell/AppSidebar.vue";
import AppRail from "@/components/shell/AppRail.vue";
import AppTouchRail from "@/components/shell/AppTouchRail.vue";
import AppPhoneBar from "@/components/shell/AppPhoneBar.vue";
import AppTabBar from "@/components/shell/AppTabBar.vue";
import AppMoreMenu from "@/components/shell/AppMoreMenu.vue";
import AppTenantSwitch from "@/components/shell/AppTenantSwitch.vue";
import AppMessageDialog from "@/components/shell/AppMessageDialog.vue";
import AppSystemBar from "@/components/shell/AppSystemBar.vue";
import AppHubTopbar from "@/components/hub/AppHubTopbar.vue";
import AppHubNotch from "@/components/hub/AppHubNotch.vue";
import AppHubSidebar from "@/components/hub/AppHubSidebar.vue";
import { visibleNavigation, findEntry, normalizePath, pageTitle, tenantSwitchAllowed, hauptfarbeWaehlbar } from "@/shell/navigation";
import { setSidebarCollapsed } from "@/shell/shellState";
import { wendeFarbschemaAn } from "@/shell/farbschema";
import { showOnStart } from "@/shell/messages";

const APP_TITLE = "Ticketing";
const NAV_WIDTH = { sidebar: 232, rail: 60, touch: 104 };

export default {
  name: "MainLayout",
  components: {
    AppSidebar,
    AppRail,
    AppTouchRail,
    AppPhoneBar,
    AppTabBar,
    AppMoreMenu,
    AppTenantSwitch,
    AppMessageDialog,
    AppSystemBar,
    AppHubTopbar,
    AppHubNotch,
    AppHubSidebar
  },
  data() {
    return { savedScroll: 0 };
  },
  computed: {
    // Shell-Variante zum Vergleich
    hub() {
      return this.$shell.variant === "hub";
    },
    // Hub: Navigation unter 768 px als Schublade (wie im Mobility Hub)
    hubMobile() {
      return this.$vuetify.breakpoint.width < 768;
    },
    hubCollapsed() {
      return this.$shell.sidebarCollapsed;
    },
    hubNavOpen() {
      return this.$shell.hubNavOpen;
    },
    // gewähltes Farbschema 'hell' | 'dunkel'
    // Farbschema und Hauptfarbe zusammen (eine Beobachtung, src/shell/farbschema.js wendet beides an)
    farbschema() {
      return this.$shell.theme + ' ' + this.$shell.hauptfarbe + ' ' + this.$shell.hauptfarbeFrei;
    },
    // Hauptfarbe nur für bconn_admin wählbar (src/shell/navigation.js)
    hauptfarbeFrei() {
      return hauptfarbeWaehlbar(this.$store.state);
    },
    shellClasses() {
      if (this.hub) {
        return ["shell--hub", { "shell--hub-collapsed": this.hubCollapsed && !this.hubMobile, "shell--hub-mobile": this.hubMobile }];
      }
      return "shell--" + this.mode;
    },
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    wide() {
      return this.$vuetify.breakpoint.width >= 1264;
    },
    // 'handy' | 'touch' | 'sidebar' | 'rail'
    mode() {
      if (this.handy) {
        return "handy";
      }
      if (this.$dichte && this.$dichte.touch) {
        return "touch";
      }
      return this.wide && !this.$shell.sidebarCollapsed ? "sidebar" : "rail";
    },
    railWidth() {
      return NAV_WIDTH[this.mode] || 0;
    },
    // reaktiv auf store.state.profile (Rechte); null-sicher beim Abmelden
    navigation() {
      return visibleNavigation(this.$router);
    },
    activePath() {
      return normalizePath(this.$route.path);
    },
    activeGroupId() {
      const found = findEntry(this.$route.path);
      return found.group ? found.group.id : null;
    },
    moreOpen() {
      return this.$shell.moreOpen;
    },
    moreGroupEntry() {
      const id = this.$shell.moreGroup;
      return id ? (this.navigation.find(group => group.id === id && group.children) || null) : null;
    },
    moreGroup() {
      return this.moreGroupEntry ? this.moreGroupEntry.id : null;
    },
    phoneTitle() {
      if (this.moreOpen) {
        return this.moreGroupEntry ? this.moreGroupEntry.title : "Mehr";
      }
      return pageTitle(this.$route.path) || APP_TITLE;
    },
    canSwitchTenant() {
      return tenantSwitchAllowed(this.$store.state);
    },
    tenantSwitchOpen: {
      get() {
        return this.$shell.tenantSwitch;
      },
      set(value) {
        this.$shell.tenantSwitch = value;
      }
    }
  },
  watch: {
    // vor farbschema: ob die Hauptfarbe wählbar ist (sonst Petrol)
    hauptfarbeFrei: {
      immediate: true,
      handler(value) {
        this.$shell.hauptfarbeFrei = value;
      }
    },
    // Farbschema und Hauptfarbe anwenden: Klassen am html-Element, Vuetify-Theme, theme-color. Läuft vor dem ersten Rendern,
    // v-app beginnt also gleich mit theme--dark; beim Abbau der Shell (beforeDestroy) wieder hell.
    farbschema: {
      immediate: true,
      handler() {
        wendeFarbschemaAn(true, this.$vuetify.theme);
      }
    },
    $route: {
      immediate: true,
      handler(to, from) {
        this.setDocumentTitle(to.path);
        // jeder Routenwechsel schließt das Mehr-Menü; eine neue Seite beginnt oben (Handy: Fenster, sonst der Inhalt
        // unter der Systemleiste, der selbst scrollt)
        const wasOpen = this.$shell.moreOpen;
        this.resetMore();
        this.$shell.hubNavOpen = false;
        if (from && !this.hub && (this.handy || wasOpen)) {
          this.$nextTick(() => window.scrollTo(0, 0));
        } else if (from && to.path !== from.path) {
          this.$nextTick(() => {
            if (this.$refs.sheet) {
              this.$refs.sheet.scrollTop = 0;
            }
          });
        }
      }
    },
    handy(value) {
      if (!value) {
        this.resetMore();
      }
    },
    // Shell gewechselt: Ebenen schließen; Grids und Karten messen in der neuen Fläche neu
    hub() {
      this.resetMore();
      this.$shell.hubNavOpen = false;
      this.$nextTick(this.notifyResize);
    },
    hubMobile(value) {
      if (!value) {
        this.$shell.hubNavOpen = false;
      }
    },
    hubNavOpen(open) {
      // Schublade: Fokus hinein, damit Esc und Tab dort beginnen
      if (open) {
        this.$nextTick(() => {
          if (this.$refs.hubDrawer) {
            this.$refs.hubDrawer.focus();
          }
        });
      }
    },
    moreOpen(open, wasOpen) {
      // Inhalt war per v-show versteckt: Grids und Karten messen nach dem Einblenden neu
      if (!open && wasOpen) {
        this.$nextTick(this.notifyResize);
      }
    }
  },
  mounted() {
    websocket.connect();
    document.addEventListener("keydown", this.onShortcut);
    // Hinweis auf die neue Version einmal je Start (bis dieser Benutzer ihn abschaltet; Status je Benutzer und Browser)
    showOnStart(this.$store.state.cognitoUsername);
  },
  beforeDestroy() {
    // Anmeldung bleibt hell
    wendeFarbschemaAn(false);
  },
  destroyed() {
    websocket.disconnect();
    document.removeEventListener("keydown", this.onShortcut);
    this.resetMore();
    this.$shell.tenantSwitch = false;
    this.$shell.hubNavOpen = false;
  },
  methods: {
    // Sprungmarke: Fokus ins Blatt, ohne zu scrollen (der nächste Tab-Schritt landet im Seiteninhalt)
    skipToContent() {
      if (this.$refs.sheet) {
        this.$refs.sheet.focus({ preventScroll: true });
      }
    },
    // Strg/⌘+B: Seitenleiste ein- und ausklappen (nur Maus ab 1264, nicht beim Schreiben in Feldern)
    onShortcut(event) {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey || String(event.key).toLowerCase() !== "b") {
        return;
      }
      if (!this.hub && (!this.wide || this.handy || (this.$dichte && this.$dichte.touch))) {
        return;
      }
      const target = event.target;
      if (target && target.closest && target.closest("input, textarea, select, [contenteditable]")) {
        return;
      }
      event.preventDefault();
      // Hub: wie im Mobility Hub – mobil die Schublade, sonst Icon-Leiste ein und aus
      if (this.hub && this.hubMobile) {
        this.toggleHubNav();
        return;
      }
      setSidebarCollapsed(!this.$shell.sidebarCollapsed);
    },
    toggleHubNav() {
      this.$shell.hubNavOpen = !this.$shell.hubNavOpen;
    },
    closeHubNav() {
      this.$shell.hubNavOpen = false;
    },
    toggleHubCollapse() {
      setSidebarCollapsed(!this.$shell.sidebarCollapsed);
    },
    // Reihenfolge wie bisher; push ohne NavigationDuplicated-Rejection (L4)
    async signOut() {
      this.$store.dispatch("signOut");
      this.$router.push("/").catch(() => {});
      await Auth.signOut();
    },
    // Browser-Tab wie im Mobility Hub: „Ticketing | Bereich“ – der Bereich ist die Gruppe der Navigation (Verwaltung,
    // Stammdaten …), sonst der Hauptpunkt (Übersicht); so bleibt der Tab beim Wechsel innerhalb eines Bereichs stabil
    setDocumentTitle(path) {
      const found = findEntry(path);
      const area = found.group ? found.group.title : pageTitle(path);
      document.title = area ? APP_TITLE + " | " + area : APP_TITLE;
    },
    resetMore() {
      this.$shell.moreOpen = false;
      this.$shell.moreGroup = null;
    },
    openMore() {
      if (this.$shell.moreOpen) {
        // Mehr erneut angetippt: zurück auf die erste Ebene
        this.$shell.moreGroup = null;
        return;
      }
      this.savedScroll = window.pageYOffset || 0;
      this.$shell.moreGroup = null;
      this.$shell.moreOpen = true;
    },
    // Ziel im Mehr-Menü angetippt
    closeMore() {
      this.resetMore();
      this.$nextTick(() => window.scrollTo(0, 0));
    },
    closeMoreGroup() {
      this.$shell.moreGroup = null;
    },
    // Ziel der unteren Navigation angetippt: ohne Routenwechsel schließt das Mehr-Menü hier (Seite wie vorher),
    // mit Routenwechsel im $route-Watcher
    onTabSelect(event) {
      if (!this.$shell.moreOpen || !event || !event.samePage) {
        return;
      }
      const y = this.savedScroll;
      this.resetMore();
      this.$nextTick(() => window.scrollTo(0, y));
    },
    notifyResize() {
      let event;
      try {
        event = new Event("resize");
      } catch (e) {
        event = document.createEvent("UIEvents");
        event.initUIEvent("resize", true, false, window, 0);
      }
      window.dispatchEvent(event);
    }
  }
};
</script>

<style>
/* Shell (Spezifikation 2.11). Selektoren mit .v-application.shell, damit sie unabhängig von der Ladereihenfolge
   vor den Vuetify-Standards gewinnen; die Anmeldung (ohne .shell) bleibt unberührt.
   Safe Area: auf Geräten ohne Aussparung ist env() = 0, die Werte entsprechen dann genau den von Vuetify
   registrierten Maßen (Drawer 232/60/104). Auf Tablets mit Home-Indikator und Telefonen im Querformat wächst der
   Drawer um die Safe Area (die !important-Regeln ersetzen dafür die Inline-Maße von Vuetify); Navigation und Blatt
   halten unten Abstand zum Home-Indikator. */

/* ---------------------------------------------------------------- Variablen am v-app */
/* Tablet und Desktop: keine feste Leiste unten (Verbindung, Stand und Version stehen in der Systemleiste); das Blatt
   hat oben und unten 8 px Abstand. Systemleiste: Polster 12 um die Bedienelemente (--h-ctl 32, Touch 44) und Linie 1,
   also 57 bzw. 69 – ihre Mitte liegt auf der Kopfzeile der Navigation (y = 37 bzw. 43). Die Hub-Shell (.shell--hub,
   src/css/redesign/hub.css) setzt eigene Werte. */
.v-application.shell:not(.shell--handy):not(.shell--hub) {
  --shell-unten: env(safe-area-inset-bottom, 0px);
  --sysbar-h: calc(var(--h-ctl) + 25px);
  --blatt-h: calc(100vh - 16px - env(safe-area-inset-bottom, 0px));
  /* klebende Leisten unten (AppSaveBar): der Inhalt im Blatt scrollt selbst */
  --sticky-unten: 0px;
}
@supports (height: 100dvh) {
  .v-application.shell:not(.shell--handy):not(.shell--hub) {
    --blatt-h: calc(100dvh - 16px - env(safe-area-inset-bottom, 0px));
  }
}
/* Breite der Navigation = linke Kante des Blatts */
.v-application.shell.shell--sidebar {
  --nav-w: calc(232px + env(safe-area-inset-left, 0px));
}
.v-application.shell.shell--rail {
  --nav-w: calc(60px + env(safe-area-inset-left, 0px));
}
.v-application.shell.shell--touch {
  --nav-w: calc(104px + env(safe-area-inset-left, 0px));
}
/* Handy: Höhe der unteren Navigation (Linie 1 + Polster 4 + Punkt 48 + Safe Area, mindestens 12); das Fenster
   scrollt, klebende Leisten bleiben über der unteren Navigation */
.v-application.shell.shell--handy {
  --shell-unten: calc(53px + max(12px, env(safe-area-inset-bottom, 0px)));
  --sticky-unten: var(--shell-unten);
}

/* ---------------------------------------------------------------- Navigation (Drawer ohne Kante, Fensterfläche) */
/* Der Drawer reicht über die volle Höhe; das Profil unten steht so weit innen wie das Logo oben (Polster unten in
   AppSidebar, AppRail und AppTouchRail) */
.v-application.shell .shell-nav.v-navigation-drawer {
  width: var(--nav-w) !important;
  background-color: var(--chrome);
}
.v-application.shell .shell-nav > .v-navigation-drawer__content {
  box-sizing: border-box;
  padding-left: env(safe-area-inset-left, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* ---------------------------------------------------------------- Hauptbereich und Blatt */
.v-application.shell .shell-main.v-main {
  background: var(--chrome);
}
.v-application.shell:not(.shell--handy):not(.shell--hub) .shell-main.v-main {
  padding-left: var(--nav-w) !important;
}
/* oben, rechts und unten je 8 px Fensterfläche um das Blatt */
.v-application.shell .shell-sheet-frame {
  padding: 8px max(8px, env(safe-area-inset-right, 0px)) calc(8px + env(safe-area-inset-bottom, 0px)) 0;
}
/* Das Blatt hat die Fensterhöhe wie ein Programmfenster: Kante und runde Ecken bleiben rundum sichtbar, der Inhalt
   wird an den Ecken geschnitten. Oben steht die Systemleiste, darunter scrollt der Inhalt (#inhalt) selbst: sticky
   Leisten kleben an seiner Unterkante, DevExtreme erkennt ihn (overflow-y: auto) als äußeren Scrollbereich, und
   fill-Seiten füllen ihn exakt (--fill-h = Blatt ohne Kanten und Systemleiste). */
.v-application.shell .shell-sheet {
  --fill-h: calc(var(--blatt-h) - 2px - var(--sysbar-h));
  height: var(--blatt-h);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--sheet);
  border-radius: var(--r-sheet);
}
/* Scrollrinne fest wie in der Systemleiste: mit klassischen Scrollleisten (Windows) enden Leiste und Inhalt rechts
   bündig, ob die Seite scrollt oder nicht */
.v-application.shell .shell-sheet > .shell-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-gutter: stable;
}
/* Druck: ganze Seite statt des sichtbaren Ausschnitts */
@media print {
  /* die Systemleiste ist im Druck ausgeblendet: fill-Seiten wieder mit voller Blatthöhe */
  .v-application.shell .shell-sheet {
    --sysbar-h: 0px;
    display: block;
    height: auto;
    overflow: visible;
  }
  .v-application.shell .shell-sheet > .shell-content {
    overflow: visible;
  }
}

/* ---------------------------------------------------------------- Handy */
.v-application.shell .shell-phone-content {
  min-height: calc(100vh - 56px);
  padding-bottom: var(--shell-unten);
  display: flex;
  flex-direction: column;
}
.v-application.shell .shell-phone-content > .shell-body,
.v-application.shell .shell-phone-content > .shell-body > .shell-content {
  flex: 1 0 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
/* Seitenfläche (AppPage) reicht bis zur unteren Navigation */
.v-application.shell .shell-phone-content > .shell-body > .shell-content > .app-page {
  flex: 1 0 auto;
}

/* ---------------------------------------------------------------- Sprungmarke */
.v-application.shell .shell-skip {
  position: fixed;
  top: 8px;
  left: 8px;
  z-index: 20;
  padding: 6px 12px;
  border: 1px solid var(--edge);
  border-radius: var(--r-ctl);
  background: var(--surface-raised);
  box-shadow: var(--sh-menu);
  color: var(--link);
  font-family: var(--font);
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  text-decoration: none;
  transform: translateY(-200%);
}
.v-application.shell .shell-skip:focus {
  transform: none;
}
/* Druck: Die feste Sprungmarke erschien sonst auf dem Blatt (unten links über dem Benutzerblock), hell wie dunkel */
@media print {
  .v-application.shell .shell-skip {
    display: none;
  }
}
/* Ziel der Sprungmarke: kein Rahmen um den ganzen Inhalt */
.v-application.shell #inhalt:focus {
  outline: none;
}
</style>
